//BlueHandsJobShortlisted.jsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import MainLayout from '../../layouts/MainLayout';
import logo from '../../assets/logo.svg';

const BlueHandsJobShortlisted = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [jobs, setJobs] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedJobId, setSelectedJobId] = useState(null);

  useEffect(() => {
    document.title = authState.isLoggedIn ? `${authState.user.name}'s dashboard` : "Think5Whys";
    fetchShortlistedJobs();
  }, [authState]);

  const fetchShortlistedJobs = useCallback(() => {
    const config = { 
        url: "/bluejobsshortlisted/my", 
        method: "post", 
        data: {userId: userId},
        headers: { Authorization: authState.token } 
      };
      fetchData(config, { showSuccessToast: false })
        .then((response) => {
          setJobs(response?.blueJobsShortlisted || []);  
        })
        .catch(error => console.error("Error fetching data:", error));
  }, [authState.token, fetchData]);
  
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  
  const deleteClicked = () => {
    setIsDeleteClicked(true);
  }
  
  const noDeleteClicked = () => {
    setIsDeleteClicked(false);
  }

  const handleDelete = (id) => {
    const config = {
      url: `/bluejobsshortlisted/${id}`,
      method: "delete",
      headers: { Authorization: authState.token }
    };
    fetchData(config).then(() => {
      setIsDeleteClicked(false);
      fetchShortlistedJobs();
    });
  }

  const handleView = (jobId) => {
    setSelectedJobId(prevId => (prevId === jobId ? null : jobId));
  };

  const handleCloseJobDetails = () => {
    setSelectedJobId(null);
  };

  const handleJobClick = (jobId) => {
    setSelectedJobId(jobId);
  };

  const handleJobShare = (jobId) => {
    setSelectedJobId(jobId);
  };

  const handleJobSave = (jobId) => {
    setSelectedJobId(jobId);
  };

  const handleJobPrint = (jobId) => {
    setSelectedJobId(jobId);
  };

  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

  const sortedJobs = [...jobs].sort((a, b) => b.jobTitle - a.jobTitle);

  return (
    <>
      <MainLayout>
        <div className='mt-12'>
          <h2 className='text-lg mt-1 mx-8 border-b border-b-gray-300 text-white'>My Shortlisted Jobs</h2>
        </div>
        <div className="my-2 mx-auto py-4">
            <div className="border border-gray-300 p-4 mt-4">
              <div className="mt-2">
                <div className="mt-2">
                  {jobs && jobs.length !== 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <div>
                      <h3 className='text-m mt-1 mx-1 text-yellow-200'>{jobs.length} Shortlisted Job Found!</h3>
                      </div>
                  </div>
                  )}
                </div>
              </div>
              {loading ? (
                <Loader />
              ) : (
                <div>
                  {jobs && jobs.length === 0 ? (
                    <div className='h-[300px] flex items-center justify-center gap-4 text-yellow-300'>
                      <div className="flex justify-between items-center">
                      <div>
                        <h3 className='text-lg mt-1 mx-2 text-orange-400'>No Shortlisted Job Record Found!</h3>
                      </div>
                  </div>
                    </div>
                  ) : (
                    sortedJobs.map((job, index) => (
                      <div key={job._id} className='bg-gray-500 my-1 p-1 text-white rounded-md shadow-md text-m'>
                        <div className='flex'>
                          <span className='mr-2'>{index + 1}.</span> {job.jobTitle}
                          <Tooltip text={"View details"} position={"top"}>
                            <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handleView(job._id)}>
                              <i className="fa-solid fa-eye"></i>
                            </span>
                          </Tooltip>
                          {/* <Tooltip text={"Edit this record"} position={"top"}>
                            <Link to={`/bluehands-job/${job._id}`} className='mr-4 text-green-600 cursor-pointer'>
                              <i className="fa-solid fa-pen"></i>
                            </Link>
                          </Tooltip> */}
                          <Tooltip text={"Delete this record"} position={"top"}>
                          <span className='text-red-500 cursor-pointer mr-2' onClick={() => deleteClicked()}>
                            <i className="fa-solid fa-trash"></i>
                          </span>
                          </Tooltip>
                          { isDeleteClicked && (
                            <div>
                              <div
                                className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center"
                                style={{ zIndex: 1000 }}
                              >
                                <div className="w-6/12 flex flex-row justify-center bg-blue-200 text-black border-b border-b-gray-300 mr-4 ml-4 p-4">
                                    <button
                                      className="bg-red-700 w-40"
                                      onClick={() =>  handleDelete(job._id) }
                                      style={{ maxWidth: "150px" }}
                                    >
                                      Confirm To Delete
                                    </button>
                                    <button
                                      className="bg-yellow-300 ml-4 text-black w-40"
                                      onClick={() =>  noDeleteClicked() }
                                      style={{ maxWidth: "150px" }}
                                    >
                                      No I Do Not Want To Delete
                                    </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {selectedJobId === job._id && (
                          <div className='bg-gray-600 text-white mt-2 px-4'>
                            <p className='text-yellow-200 mt-2'>{job.jobTitle}</p>
                            <p className='mt-2'><strong>Employer Name:</strong>{job.employerName}</p>
                            <p className='mt-2'><strong>Date Shortlisted:</strong> {formatDateTime(job.postedDate)}</p>
                            <p className='mt-2'><strong>Remarks:</strong>{job.shortlistedRemarks}</p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
      </MainLayout>
    </>
  )
}

export default BlueHandsJobShortlisted